import React, { useState, useEffect, forwardRef } from "react";
import { useInView } from "react-intersection-observer";

const SIZE_BREAKPOINTS = {
  xl: 1600,
  l: 1201,
  m: 1024,
  s: 768,
  xs: 480,
};

const ImageLoader = forwardRef((props, ref) => {
  const {
    source,
    viewports,
    viewportsRatio,
    alt,
    imgProps = {},
    enforceRatio = false,
    defaultImage,
    ratio,
    shouldLoad = true,
    height = 'auto',
    width = 'auto',
  } = props;
  const getViewport = (size) => {
    if (size >= SIZE_BREAKPOINTS.xl) return "xxl";
    else if (size >= SIZE_BREAKPOINTS.l) return "xl";
    else if (size >= SIZE_BREAKPOINTS.m) return "l";
    else if (size >= SIZE_BREAKPOINTS.s) return "m";
    else if (size >= SIZE_BREAKPOINTS.xs) return "s";
    else return "xs";
  };

  const autoFillViewport = (desiredViewport, obj) => {
    if (obj.hasOwnProperty(desiredViewport)) {
      return obj[desiredViewport];
    } else if (desiredViewport == SIZE_BREAKPOINTS.xs) {
      return null;
    }
    const nextKey = Object.keys(
      SIZE_BREAKPOINTS
    ).reduce((acc, item, idx, arr) =>
      desiredViewport == arr[idx - 1] ? item : acc
    );
    return autoFillViewport(nextKey, obj);
  };

  const [sourceLoaded, setSourceLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [currentViewport, setCurrentViewport] = useState(
    getViewport(window.innerWidth)
  );

  const currentSource = viewports
    ? viewports.hasOwnProperty(currentViewport)
      ? viewports[currentViewport]
      : autoFillViewport(currentViewport, viewports) || source
    : source;

  const currentRatio = viewportsRatio
    ? viewportsRatio.hasOwnProperty(currentViewport)
      ? viewportsRatio[currentViewport]
      : autoFillViewport(currentViewport, viewportsRatio) || ratio
    : ratio || "1:1";

  useEffect(() => {
    let mounted = true;
    if (shouldLoad) {
      new Promise((resolve, reject) => {
        const img = new Image();
        img.src = currentSource;
        img.onload = resolve;
        img.onerror = reject;
      })
        .then(() => {
          if (mounted) {
            setSourceLoaded(true);
            setHasError(false);
          }
        })
        .catch(() => {
          if (mounted) {
            setSourceLoaded(true);
            setHasError(true);
          }
        });
    }
    return () => (mounted = false);
  }, [currentSource, shouldLoad]);

  useEffect(() => {
    let timeout = null;
    const listener = () => {
      clearTimeout(timeout);
      timeout = setTimeout(
        () => setCurrentViewport(getViewport(window.innerWidth)),
        200
      );
    };
    window.addEventListener("resize", listener);
    return () => window.removeEventListener("resize", listener);
  }, [1]);

  const imgSrc = hasError ? defaultImage : currentSource

  if (!enforceRatio && sourceLoaded) {
    return imgSrc ? (
      <img
        src={imgSrc}
        alt={alt}
        height={height}
        width={width}
        {...imgProps}
      />
    ) : null;
  } else {
    return (
      <div ref={ref} className="image-loading">
        <span
          style={{
            display: "block",
            position: 'relative',
            width: width !== 'auto' ? width : "100%",
            paddingTop: height !== 'auto' ? height
              : "calc(" +
                currentRatio.split(":")[1] +
                " / " +
                currentRatio.split(":")[0] +
                " * 100%)",
          }}
        />
        {imgSrc && (
          <img
            src={hasError ? defaultImage : currentSource}
            alt={alt}
            height={height}
            width={width}
            {...imgProps}
            style={{
              ...imgProps.style,
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%'
            }}
          />
        )}
      </div>
    );
  }
});

const ImageLoaderInViewWrapper = (props) => {
  const [ref, inView, entry] = useInView({
    treshold: 0.05,
  });

  return (
    <ImageLoader
      {...props}
      ref={ref}
      shouldLoad={inView ? props.shouldLoad : false}
    />
  );
};

export default ImageLoaderInViewWrapper;
